import { useLocation, useNavigate, useParams } from "@tanstack/react-router";
import type { MerchantOnboardingStatus, WorkspaceRole } from "@unbox/api";
import { Inbox02, RefreshCw05 } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import { useMe } from "@/api/me";
import { MerchantLogo } from "@/components/application/merchant-logo/merchant-logo";
import { LogoPlaceholder } from "@/components/ui/MerchantLogo";
import { getLastAccessed } from "@/utils/last-accessed";
import { resolveSidebarLogo } from "./sidebar-logo";
import { type WorkspaceEntry, workspaceEntry } from "./workspace-entry";

const ENTRY_PATHS = {
  workspace: "/workspace/$workspaceId/overview",
  portfolio: "/workspace/$workspaceId/portfolio/overview",
} as const satisfies Record<WorkspaceEntry, string>;

const isEntryPath = (path: string, workspaceId: string, entry: WorkspaceEntry): boolean => {
  const prefix = `/workspace/${workspaceId}/`;
  if (!path.startsWith(prefix) || path.includes("/merchant/")) return false;
  return path.startsWith(`${prefix}portfolio/`) === (entry === "portfolio");
};

type MerchantOption = {
  id: string;
  name: string;
  logoUrl: string | null;
  region?: string | null;
  onboardingStatus?: MerchantOnboardingStatus;
};
type WorkspaceOption = {
  id: string;
  name: string;
  logoUrl: string | null;
  role: WorkspaceRole;
  merchants: MerchantOption[];
};

// Renders a logo for a workspace/merchant row using the same reliable <img>-with-
// initials-fallback component used across the console (e.g. /admin/customers), so
// menu logos load straight away instead of the intermittent skeleton behaviour.
const ItemLogo = ({
  logoUrl,
  name,
  fallbackIcon,
}: {
  logoUrl: string | null;
  name: string;
  fallbackIcon?: React.ComponentType<{ className?: string }>;
}) => {
  if (!logoUrl && (fallbackIcon || !name))
    return <LogoPlaceholder className="w-8 h-8 rounded flex-shrink-0" icon={fallbackIcon} />;
  return (
    <MerchantLogo name={name} logoUrl={logoUrl} size="sm" className="size-8 rounded bg-white object-contain shrink-0" />
  );
};

const RegionBadge = ({ region }: { region?: string | null }) => {
  if (!region) return null;
  return (
    <span className="ml-auto flex-shrink-0 rounded-full border border-gray-200 px-2 py-0.5 text-xs font-medium text-gray-500">
      {region}
    </span>
  );
};

/**
 * The row above the brands. Workspace-level permissions surface the workspace itself;
 * everyone else reaches the same brands through their portfolio.
 */
const ScopeRowContent = ({ workspace, entry }: { workspace: WorkspaceOption; entry: WorkspaceEntry }) => (
  <>
    <ItemLogo
      logoUrl={workspace.logoUrl}
      name={workspace.name}
      fallbackIcon={entry === "workspace" ? undefined : Inbox02}
    />
    <span className="text-gray-700 font-semibold text-sm text-left">
      {entry === "workspace" ? `${workspace.name} Workspace` : "Your Portfolio"}
    </span>
  </>
);

const CurrentSelection = ({ merchantId, workspaceId }: { merchantId: string; workspaceId?: string }) => {
  const { data } = useMe();
  const path = window.location.pathname;
  const isPortfolio = path.startsWith("/portfolio");
  // Workspace context (e.g. a Workspace Admin's Overview/Safety nav, UNB-1953) shows the
  // workspace icon. As soon as a brand is selected — even from within that workspace, at
  // /workspace/$workspaceId/merchant/$merchantId — the icon reverts to the brand's.
  const isWorkspaceOnly = !!workspaceId && !path.includes("/merchant/");

  const selection = resolveSidebarLogo(data, {
    // `merchantId` falls back to the stored api_key, which can still hold a brand the user
    // is no longer viewing, so it is dropped here to let the workspace logo win.
    merchantId: isWorkspaceOnly ? "" : merchantId,
    workspaceId,
    isPortfolio,
    lastAccessedWorkspaceId: getLastAccessed()?.workspaceId,
  });

  if (selection.kind === "portfolio") return <LogoPlaceholder className="w-8 h-8 rounded" icon={Inbox02} />;

  return <ItemLogo logoUrl={selection.logoUrl} name={selection.name} />;
};

export const MerchantSelector = ({ onOpen }: { onOpen?: () => void }) => {
  const { data } = useMe();
  const params = useParams({ strict: false }) as { merchantId?: string; workspaceId?: string };
  const currentMerchantId = params.merchantId ?? localStorage.getItem("api_key") ?? "";
  const currentWorkspaceId = params.workspaceId;

  const [isOpen, setIsOpen] = useState(false);
  const [hoveredWorkspaceId, setHoveredWorkspaceId] = useState<string | null>(null);
  const [flyoutTop, setFlyoutTop] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const _workspaceButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const navigate = useNavigate();
  const location = useLocation();

  // Brands are reached exclusively through workspaces.
  const directMerchants: MerchantOption[] = [];
  const workspaces: WorkspaceOption[] = data?.workspaces ?? [];

  // For direct merchants (no workspace context) — strips any workspace prefix.
  const navigateToMerchant = (merchantId: string) => {
    const rawPath = location.pathname;
    const path = rawPath.replace(/^\/workspace\/[^/]+/, "");
    if (path.includes("/merchant/")) {
      const newPath = path.replace(/\/merchant\/[^/]+/, `/merchant/${merchantId}`);
      navigate({ to: newPath as string });
    } else {
      navigate({ to: "/merchant/$merchantId", params: { merchantId } });
    }
    setIsOpen(false);
  };

  // For workspace-scoped brands — preserves the workspace ID in the URL.
  const navigateToWorkspaceMerchant = (workspaceId: string, merchantId: string) => {
    const rawPath = location.pathname;
    if (rawPath.match(/^\/workspace\/[^/]+\/merchant\/[^/]+/)) {
      // Already on a workspace merchant path — swap both IDs, keep the rest of the path.
      const newPath = rawPath
        .replace(/\/workspace\/[^/]+/, `/workspace/${workspaceId}`)
        .replace(/\/merchant\/[^/]+/, `/merchant/${merchantId}`);
      navigate({ to: newPath as string });
    } else if (rawPath.includes("/merchant/")) {
      // On a plain /merchant/... path — prefix with the workspace.
      const suffix = rawPath.replace(/^\/merchant\/[^/]+/, "");
      navigate({ to: `/workspace/${workspaceId}/merchant/${merchantId}${suffix}` as string });
    } else {
      navigate({ to: "/workspace/$workspaceId/merchant/$merchantId", params: { workspaceId, merchantId } });
    }
    setIsOpen(false);
  };

  const navigateToWorkspaceEntry = (workspace: WorkspaceOption, entry: WorkspaceEntry) => {
    navigate({ to: ENTRY_PATHS[entry], params: { workspaceId: workspace.id } });
    setIsOpen(false);
  };

  const renderScopeRow = (workspace: WorkspaceOption) => {
    const entry = workspaceEntry(workspace);
    if (!entry) return null;
    const isCurrent = isEntryPath(location.pathname, workspace.id, entry);
    return (
      <button
        type="button"
        onClick={() => navigateToWorkspaceEntry(workspace, entry)}
        className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-gray-100 transition-colors border-b border-gray-200 ${
          isCurrent ? "bg-gray-100" : ""
        }`}
      >
        <ScopeRowContent workspace={workspace} entry={entry} />
      </button>
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHoveredWorkspaceId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const hasContent = workspaces.length > 0 || directMerchants.length > 0;

  // When the user belongs to exactly one workspace and has no direct merchants,
  // skip the workspace tier and show brands directly (URL still carries workspaceId).
  const singleWorkspace = workspaces.length === 1 && directMerchants.length === 0 ? workspaces[0] : null;

  const renderBrandRow = (merchant: MerchantOption, workspaceId: string) => {
    const isAnalysing = merchant.onboardingStatus === "scanning";
    return (
      <button
        key={merchant.id}
        type="button"
        disabled={isAnalysing}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          navigateToWorkspaceMerchant(workspaceId, merchant.id);
        }}
        className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-gray-100 transition-colors border-b border-gray-200 last:border-b-0 disabled:cursor-not-allowed disabled:hover:bg-transparent ${
          currentMerchantId === merchant.id && currentWorkspaceId === workspaceId ? "bg-gray-100" : ""
        }`}
      >
        <span className={isAnalysing ? "opacity-50" : undefined}>
          <ItemLogo logoUrl={merchant.logoUrl ?? null} name={merchant.name} />
        </span>
        <span className="flex flex-col items-start min-w-0">
          <span className={`font-semibold text-sm text-left ${isAnalysing ? "text-gray-400" : "text-gray-700"}`}>
            {merchant.name}
          </span>
          {isAnalysing && (
            <span className="flex items-center gap-1 text-xs text-warning-600 whitespace-nowrap">
              <RefreshCw05 className="size-3.5 animate-spin [animation-duration:1.6s]" />
              Analysing all videos
            </span>
          )}
        </span>
        <RegionBadge region={merchant.region} />
      </button>
    );
  };

  return (
    <div ref={dropdownRef} className="absolute inset-0">
      {/* Trigger */}
      <button
        type="button"
        aria-label="Brand selector"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpen?.();
          setIsOpen(!isOpen);
          setHoveredWorkspaceId(null);
        }}
        className="w-full h-full bg-transparent flex items-center justify-center hover:bg-gray-100/50 transition-colors rounded-sm"
      >
        <CurrentSelection merchantId={currentMerchantId} workspaceId={currentWorkspaceId} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute bottom-0 left-full ml-2 z-[9999] flex">
          <div
            className="bg-white border border-gray-200 rounded-lg shadow-none overflow-auto min-w-[200px] max-h-96"
            style={{ scrollbarWidth: "none" }}
          >
            <div className="bg-gray-50">
              {!hasContent && <div className="px-3 py-2 text-gray-500 text-sm">No companies available</div>}

              {/* ── Single-workspace shortcut: skip workspace tier, show brands directly ── */}
              {singleWorkspace && (
                <>
                  {renderScopeRow(singleWorkspace)}
                  {singleWorkspace.merchants.map((merchant) => renderBrandRow(merchant, singleWorkspace.id))}
                </>
              )}

              {/* ── Multi-workspace / direct-merchant layout ── */}
              {!singleWorkspace && (
                <>
                  {/* Workspaces with flyout */}
                  {workspaces.map((workspace) => {
                    const flyoutId = `ws-flyout-${workspace.id}`;
                    const isExpanded = hoveredWorkspaceId === workspace.id;

                    const openFlyout = (btn: HTMLButtonElement) => {
                      if (dropdownRef.current) {
                        const btnRect = btn.getBoundingClientRect();
                        const containerRect = dropdownRef.current.getBoundingClientRect();
                        setFlyoutTop(btnRect.bottom - containerRect.bottom);
                      }
                      setHoveredWorkspaceId(workspace.id);
                    };

                    return (
                      <button
                        key={workspace.id}
                        ref={(el) => {
                          _workspaceButtonRefs.current[workspace.id] = el;
                        }}
                        type="button"
                        aria-expanded={isExpanded}
                        aria-controls={workspace.merchants.length > 0 ? flyoutId : undefined}
                        onMouseEnter={(e) => openFlyout(e.currentTarget)}
                        onFocus={(e) => openFlyout(e.currentTarget)}
                        onClick={(e) => (isExpanded ? setHoveredWorkspaceId(null) : openFlyout(e.currentTarget))}
                        className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-gray-100 transition-colors border-b border-gray-200 last:border-b-0 ${
                          currentWorkspaceId === workspace.id ? "bg-gray-100" : ""
                        }`}
                      >
                        <ItemLogo logoUrl={workspace.logoUrl} name={workspace.name} />
                        <span className="text-gray-700 font-semibold text-sm text-left flex-1">{workspace.name}</span>
                        {workspace.merchants.length > 0 && <span className="text-gray-400 text-xs">›</span>}
                      </button>
                    );
                  })}

                  {/* Direct merchants (not in any workspace) */}
                  {directMerchants.map((merchant) => (
                    <button
                      key={merchant.id}
                      type="button"
                      onMouseEnter={() => setHoveredWorkspaceId(null)}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigateToMerchant(merchant.id);
                      }}
                      className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-gray-100 transition-colors border-b border-gray-200 last:border-b-0 ${
                        currentMerchantId === merchant.id && !currentWorkspaceId ? "bg-gray-100" : ""
                      }`}
                    >
                      <ItemLogo logoUrl={merchant.logoUrl} name={merchant.name} />
                      <span className="text-gray-700 font-semibold text-sm text-left">{merchant.name}</span>
                      <RegionBadge region={merchant.region} />
                    </button>
                  ))}

                  {/* Workspace brand flyout — anchored to the hovered workspace row */}
                  {hoveredWorkspaceId &&
                    (() => {
                      const workspace = workspaces.find((w) => w.id === hoveredWorkspaceId);
                      if (!workspace || workspace.merchants.length === 0) return null;
                      return (
                        <div
                          id={`ws-flyout-${workspace.id}`}
                          className="absolute left-full bg-white border border-gray-200 rounded-lg shadow-none overflow-auto min-w-[200px] max-h-96"
                          style={{ bottom: `${-flyoutTop}px`, scrollbarWidth: "none" }}
                        >
                          <div className="bg-gray-50">
                            {renderScopeRow(workspace)}
                            {workspace.merchants.map((merchant) => renderBrandRow(merchant, workspace.id))}
                          </div>
                        </div>
                      );
                    })()}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
